import { useMemo, useRef, useState } from "react";
import type { ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import { representativePoint } from "../model/inp/selection.ts";
import {
  buildTangentPlaneCrs,
  fitSimilarityTransform,
  type ControlPoint,
  type SimilarityFit,
} from "../model/geo/controlPointFit.ts";
import { fromLonLat } from "../model/geo/reproject.ts";
import { kindLabelKey } from "../i18n/fieldLabels.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { ControlPointPickerMap } from "./ControlPointPickerMap.tsx";
import { InfoIcon } from "./InfoIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

// Audit finding: the previous palette put a saturated alizarin red
// (#c0392b) in the second slot, and a red-on-white numbered badge
// consistently reads as an error/delete affordance rather than
// "point 2 in this sequential list". The Okabe-Ito colorblind-safe
// palette gives the audit's own suggested pair (#0072b2 + #e69f00)
// for the first two, and its other members extend the scale for
// projects with three or more control points — cycled with `%` below
// when more than 6 are added. Yellow (#f0e442) and black (#000000)
// from the full 8-color Okabe-Ito set are deliberately skipped: white
// marker text (see ControlPointPickerMap.tsx) has poor contrast on
// yellow, and black blends with typical marker outlines on the map.
const PALETTE = [
  "#0072b2", // blue
  "#e69f00", // orange
  "#009e73", // bluish green
  "#cc79a7", // reddish purple
  "#56b4e9", // sky blue
  "#d55e00", // vermilion
];

/** Below this many complete rows, a residual isn't meaningful: 2 points is an exact fit by construction (residuals are ~0 floating-point noise). */
const MIN_ROWS_FOR_RESIDUAL_WARNING = 3;
/** A residual more than 3x the median is flagged as a likely mis-click. Floored at 0.5m so a near-perfect fit doesn't flag itself over sub-meter noise. */
const RESIDUAL_WARNING_MULTIPLE = 3;
const RESIDUAL_WARNING_FLOOR_M = 0.5;

interface ControlPointRow {
  id: string;
  ref: ElementRef | null;
  target: { lat: number; lng: number } | null;
}

function refKey(ref: ElementRef): string {
  return `${ref.category}-${ref.name}`;
}

/** An inline "pick a node/raingage by name" combobox — needs to sit visibly inline as part of a row, not behind an extra click like ElementSearch.tsx's popover. */
function ElementPicker({
  elements,
  value,
  onChange,
}: {
  elements: ElementRef[];
  value: ElementRef | null;
  onChange: (ref: ElementRef) => void;
}) {
  const t = useT();
  const [text, setText] = useState(value?.name ?? "");
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    const pool = q ? elements.filter((e) => e.name.toLowerCase().includes(q)) : elements;
    return pool.slice(0, 20);
  }, [elements, text]);

  return (
    <div className="control-point-element-picker">
      <input
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // A click on a result needs to register before the list unmounts.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t("georeference.pickElementPlaceholder")}
      />
      {open && matches.length > 0 && (
        <ul className="control-point-element-picker-results">
          {matches.map((ref) => {
            const kindKey = kindLabelKey(ref.kind);
            return (
              <li key={refKey(ref)}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(ref);
                    setText(ref.name);
                    setOpen(false);
                  }}
                >
                  {ref.name}
                  {kindKey && (
                    <span className="control-point-element-picker-kind">{t(kindKey)}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * A one-shot tool, not a stored setting: the user pairs 2+ existing
 * nodes/raingages with their real-world location on an embedded map, a
 * similarity transform is fit, and `onApply` rewrites every project
 * coordinate into real WGS84 degrees — nothing about the control points
 * themselves is kept once this closes. Nodes/raingages only among pickable
 * elements: the only two categories with a single authoritative stored
 * point (`representativePoint`'s link/subcatchment cases are derived, not a
 * literal `[COORDINATES]`/`[SYMBOLS]` row).
 */
export function GeoreferenceDialog({
  project,
  onApply,
  onClose,
}: {
  project: SwmmProject;
  onApply: (fit: SimilarityFit, tangentPlaneCrs: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const language = useLanguage();
  const nextIdRef = useRef(0);
  function newId(): string {
    nextIdRef.current += 1;
    return `cp-${nextIdRef.current}`;
  }
  const [rows, setRows] = useState<ControlPointRow[]>(() => [
    { id: newId(), ref: null, target: null },
    { id: newId(), ref: null, target: null },
  ]);
  const [activeRowId, setActiveRowId] = useState<string | null>(() => rows[0].id);

  const pickableElements = useMemo<ElementRef[]>(
    () => [
      ...project.raingages.map((g): ElementRef => ({
        category: "raingage",
        kind: "raingage",
        name: g.name,
      })),
      ...project.nodes.map((n): ElementRef => ({ category: "node", kind: n.kind, name: n.name })),
    ],
    [project]
  );

  function addRow() {
    const id = newId();
    setRows((current) => [...current, { id, ref: null, target: null }]);
    setActiveRowId(id);
  }

  function removeRow(id: string) {
    setRows((current) => current.filter((r) => r.id !== id));
    setActiveRowId((current) => (current === id ? null : current));
  }

  function setRowRef(id: string, ref: ElementRef) {
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ref } : r)));
  }

  function handleMapPick(lat: number, lng: number) {
    if (!activeRowId) return;
    setRows((current) =>
      current.map((r) => (r.id === activeRowId ? { ...r, target: { lat, lng } } : r))
    );
  }

  const fitResult = useMemo(() => {
    const complete = rows.filter(
      (r): r is ControlPointRow & { ref: ElementRef; target: { lat: number; lng: number } } =>
        r.ref !== null && r.target !== null
    );
    if (complete.length < 2) return null;
    const lon0 = complete.reduce((sum, r) => sum + r.target.lng, 0) / complete.length;
    const lat0 = complete.reduce((sum, r) => sum + r.target.lat, 0) / complete.length;
    const tangentPlaneCrs = buildTangentPlaneCrs(lon0, lat0);
    const points: (ControlPoint & { rowId: string })[] = [];
    for (const row of complete) {
      const local = representativePoint(project, row.ref);
      if (!local) continue; // shouldn't happen for a node/raingage that's really in `project`, but never crash on it
      points.push({
        rowId: row.id,
        local,
        target: fromLonLat(row.target.lng, row.target.lat, tangentPlaneCrs),
      });
    }
    const fit = fitSimilarityTransform(points);
    if (!fit) return null;
    const residualByRowId = new Map(points.map((p, i) => [p.rowId, fit.residuals[i]]));
    return { fit, tangentPlaneCrs, residualByRowId };
  }, [rows, project]);

  const residualWarningThreshold = useMemo(() => {
    if (!fitResult || fitResult.residualByRowId.size < MIN_ROWS_FOR_RESIDUAL_WARNING) return null;
    const sorted = [...fitResult.residualByRowId.values()].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return Math.max(median * RESIDUAL_WARNING_MULTIPLE, RESIDUAL_WARNING_FLOOR_M);
  }, [fitResult]);

  return (
    <Dialog title={t("georeference.title")} onClose={onClose} wide>
      <p className="property-panel-hint">
        {t("georeference.hint")}{" "}
        {/* PII/Privacy (Medium), deep-review: the map below (ControlPointPickerMap.tsx)
            loads OpenStreetMap tiles, an undisclosed third-party request —
            same compact info-icon+tooltip pattern as fields.tsx's own
            NumberField/SelectField, not a permanent wall of text. */}
        <InfoIcon text={t("georeference.osmMapDisclosure")} />
      </p>
      <ul className="control-point-rows">
        {rows.map((row, index) => {
          const color = PALETTE[index % PALETTE.length];
          const residual = fitResult?.residualByRowId.get(row.id);
          const flagged =
            residual !== undefined &&
            residualWarningThreshold !== null &&
            residual > residualWarningThreshold;
          const usedElsewhere = new Set(
            rows.filter((r) => r.id !== row.id && r.ref).map((r) => refKey(r.ref!))
          );
          const availableElements = pickableElements.filter((e) => !usedElsewhere.has(refKey(e)));
          return (
            <li
              key={row.id}
              className={
                row.id === activeRowId
                  ? "control-point-row control-point-row-active"
                  : "control-point-row"
              }
              onClick={() => setActiveRowId(row.id)}
            >
              <span className="control-point-badge" style={{ background: color }}>
                {index + 1}
              </span>
              <ElementPicker
                elements={availableElements}
                value={row.ref}
                onChange={(ref) => setRowRef(row.id, ref)}
              />
              <span className="control-point-target">
                {row.target
                  ? t("georeference.targetCoords", {
                      lat: formatNumber(row.target.lat, language, 5),
                      lon: formatNumber(row.target.lng, language, 5),
                    })
                  : t("georeference.noTargetYet")}
              </span>
              {residual !== undefined && (
                <span
                  className={
                    flagged
                      ? "control-point-residual control-point-residual-warning"
                      : "control-point-residual"
                  }
                >
                  {t("georeference.residual", { meters: formatNumber(residual, language, 1) })}
                </span>
              )}
              {rows.length > 2 && (
                <button
                  className="button-icon button-icon-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRow(row.id);
                  }}
                  aria-label={t("georeference.removePoint")}
                  title={t("georeference.removePoint")}
                >
                  <TrashIcon />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button className="button" onClick={addRow}>
        <PlusIcon />
        {t("georeference.addPoint")}
      </button>
      <ControlPointPickerMap
        rows={rows.map((r, i) => ({
          id: r.id,
          label: String(i + 1),
          color: PALETTE[i % PALETTE.length],
          target: r.target,
        }))}
        onPick={handleMapPick}
      />
      <DialogActions
        primary={{
          label: t("georeference.apply"),
          onClick: () => {
            if (fitResult) onApply(fitResult.fit, fitResult.tangentPlaneCrs);
          },
          disabled: !fitResult,
        }}
        onClose={onClose}
      />
    </Dialog>
  );
}
